import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './TicTacToe.css';

const WINNING_LINES = [
  [0,1,2],[3,4,5],[6,7,8],
  [0,3,6],[1,4,7],[2,5,8],
  [0,4,8],[2,4,6],
];

function checkWinner(board) {
  for (const [a, b, c] of WINNING_LINES) {
    if (board[a] && board[a] === board[b] && board[a] === board[c]) {
      return { winner: board[a], line: [a, b, c] };
    }
  }
  return board.every((c) => c) ? { winner: 'draw', line: null } : null;
}

function minimax(board, isMax, alpha, beta) {
  const result = checkWinner(board);
  if (result) {
    if (result.winner === 'O') return 10;
    if (result.winner === 'X') return -10;
    return 0;
  }

  if (isMax) {
    let best = -Infinity;
    for (let i = 0; i < 9; i++) {
      if (!board[i]) {
        board[i] = 'O';
        best = Math.max(best, minimax(board, false, alpha, beta));
        board[i] = null;
        alpha = Math.max(alpha, best);
        if (beta <= alpha) break;
      }
    }
    return best;
  } else {
    let best = Infinity;
    for (let i = 0; i < 9; i++) {
      if (!board[i]) {
        board[i] = 'X';
        best = Math.min(best, minimax(board, true, alpha, beta));
        board[i] = null;
        beta = Math.min(beta, best);
        if (beta <= alpha) break;
      }
    }
    return best;
  }
}

function getBestMove(board, difficulty) {
  // Easy: random
  if (difficulty === 'easy') {
    const empty = board.map((c, i) => c ? null : i).filter((i) => i !== null);
    return empty[Math.floor(Math.random() * empty.length)];
  }
  // Medium: 50% optimal
  if (difficulty === 'medium' && Math.random() < 0.5) {
    const empty = board.map((c, i) => c ? null : i).filter((i) => i !== null);
    return empty[Math.floor(Math.random() * empty.length)];
  }
  // Hard: minimax
  let bestVal = -Infinity;
  let bestMove = -1;
  for (let i = 0; i < 9; i++) {
    if (!board[i]) {
      board[i] = 'O';
      const val = minimax(board, false, -Infinity, Infinity);
      board[i] = null;
      if (val > bestVal) { bestVal = val; bestMove = i; }
    }
  }
  return bestMove;
}

function TicTacToe() {
  const navigate = useNavigate();
  const [board, setBoard] = useState(Array(9).fill(null));
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [result, setResult] = useState(null);
  const [difficulty, setDifficulty] = useState('hard');
  const [mode, setMode] = useState('ai');
  const [scores, setScores] = useState({ x: 0, o: 0, draw: 0 });
  const [winLine, setWinLine] = useState(null);

  const handleMove = useCallback((index) => {
    if (board[index] || result) return;
    if (mode === 'ai' && !isPlayerTurn) return;

    const newBoard = [...board];
    newBoard[index] = mode === '2p' ? (isPlayerTurn ? 'X' : 'O') : 'X';
    setBoard(newBoard);

    const check = checkWinner(newBoard);
    if (check) {
      setResult(check);
      setWinLine(check.line);
      if (check.winner === 'X') setScores((s) => ({ ...s, x: s.x + 1 }));
      else if (check.winner === 'O') setScores((s) => ({ ...s, o: s.o + 1 }));
      else setScores((s) => ({ ...s, draw: s.draw + 1 }));
      return;
    }

    setIsPlayerTurn(!isPlayerTurn);
  }, [board, result, isPlayerTurn, mode]);

  // AI move
  useEffect(() => {
    if (mode !== 'ai' || isPlayerTurn || result) return;
    const timer = setTimeout(() => {
      const move = getBestMove([...board], difficulty);
      if (move === -1 || move === undefined) return;
      const newBoard = [...board];
      newBoard[move] = 'O';
      setBoard(newBoard);

      const check = checkWinner(newBoard);
      if (check) {
        setResult(check);
        setWinLine(check.line);
        if (check.winner === 'O') setScores((s) => ({ ...s, o: s.o + 1 }));
        else if (check.winner === 'draw') setScores((s) => ({ ...s, draw: s.draw + 1 }));
      }
      setIsPlayerTurn(true);
    }, 400);
    return () => clearTimeout(timer);
  }, [isPlayerTurn, board, result, mode, difficulty]);

  const handleReset = () => {
    setBoard(Array(9).fill(null));
    setIsPlayerTurn(true);
    setResult(null);
    setWinLine(null);
  };

  const handleNewMatch = () => {
    handleReset();
    setScores({ x: 0, o: 0, draw: 0 });
  };

  return (
    <div className="ttt-app">
      <div className="ttt-topbar">
        <div className="ttt-topbar-left">
          <button className="ttt-back-btn" onClick={() => navigate('/demo/games')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="ttt-title">Tic-Tac-Toe</h1>
        </div>
        <div className="ttt-topbar-right">
          <div className="ttt-mode-toggle">
            <button className={mode === 'ai' ? 'active' : ''} onClick={() => { setMode('ai'); handleReset(); }}>vs AI</button>
            <button className={mode === '2p' ? 'active' : ''} onClick={() => { setMode('2p'); handleReset(); }}>2 Players</button>
          </div>
          {mode === 'ai' && (
            <div className="ttt-diff-toggle">
              {['easy', 'medium', 'hard'].map((d) => (
                <button key={d} className={difficulty === d ? 'active' : ''} onClick={() => { setDifficulty(d); handleReset(); }}>
                  {d.charAt(0).toUpperCase() + d.slice(1)}
                </button>
              ))}
            </div>
          )}
          <button className="ttt-action-btn" onClick={handleNewMatch}>Reset Scores</button>
        </div>
      </div>

      <div className="ttt-container">
        <div className="ttt-scoreboard">
          <div className={`ttt-score-card ${isPlayerTurn && !result ? 'active' : ''}`}>
            <span className="ttt-score-symbol x">X</span>
            <span className="ttt-score-name">{mode === 'ai' ? 'You' : 'Player 1'}</span>
            <span className="ttt-score-num">{scores.x}</span>
          </div>
          <div className={`ttt-score-card draw`}>
            <span className="ttt-score-symbol">=</span>
            <span className="ttt-score-name">Draw</span>
            <span className="ttt-score-num">{scores.draw}</span>
          </div>
          <div className={`ttt-score-card ${!isPlayerTurn && !result ? 'active' : ''}`}>
            <span className="ttt-score-symbol o">O</span>
            <span className="ttt-score-name">{mode === 'ai' ? 'AI' : 'Player 2'}</span>
            <span className="ttt-score-num">{scores.o}</span>
          </div>
        </div>

        <div className="ttt-board">
          {board.map((cell, i) => (
            <button
              key={i}
              className={`ttt-cell ${cell ? 'filled' : ''} ${cell === 'X' ? 'x' : cell === 'O' ? 'o' : ''} ${winLine?.includes(i) ? 'win' : ''}`}
              onClick={() => handleMove(i)}
              disabled={!!cell || !!result}
            >
              {cell && <span className="ttt-mark">{cell}</span>}
            </button>
          ))}
        </div>

        {result && (
          <div className="ttt-result">
            <span className="ttt-result-text">
              {result.winner === 'draw' ? "It's a draw!" :
               result.winner === 'X' ? (mode === 'ai' ? '🎉 You win!' : 'Player 1 wins!') :
               (mode === 'ai' ? '🤖 AI wins!' : 'Player 2 wins!')}
            </span>
            <button className="ttt-play-again" onClick={handleReset}>Play Again</button>
          </div>
        )}

        {!result && (
          <div className="ttt-turn-info">
            {mode === 'ai'
              ? (isPlayerTurn ? 'Your turn (X)' : 'AI is thinking...')
              : (isPlayerTurn ? 'Player 1 turn (X)' : 'Player 2 turn (O)')
            }
          </div>
        )}
      </div>
    </div>
  );
}

export default TicTacToe;
